/**
 * Google Tag Manager (GTM) DataLayer Utilities
 *
 * Type-safe helpers for pushing events to the GTM dataLayer. All tracking
 * events are prefixed with `brew_` for easy identification in GTM.
 *
 * Usage:
 *   import { pushToDataLayer } from "@/lib/site/gtm";
 *   pushToDataLayer({ event: "brew_checkout_started", ... });
 *
 * GTM Configuration:
 *   - Create a Custom Event trigger for each event name.
 *   - Create Data Layer Variables (dlv) for each property you want to use.
 *   - Map these to your Google Ads conversion tags or other destinations.
 *
 * Container ID is centralized in `lib/site/gtm-config.ts`. The container
 * script boots per route group via `<GtmRouteScope />`
 * (`components/site/gtm-route-scope.tsx`) — NOT in the root layout — and
 * `pushToDataLayer` lazy-boots it (`lib/site/gtm-boot.ts`) when a `brew_*`
 * conversion event fires on a route without the eager mount, so conversion
 * attribution is route-independent.
 *
 * Dev-mode behavior:
 *   - Events are discarded and NOT pushed to the dataLayer by default, so
 *     analytics aren't polluted and customer identifiers do not reach logs.
 *   - To exercise the live push path while developing (e.g. validating
 *     a new tag), set `NEXT_PUBLIC_GTM_DEBUG=1`.
 */

import { addRumError } from '@brew/observability/browser'
import { FREE_PLAN_NAMES, getPlanDisplayName } from '@/lib/billing/plans'
import { isFlagValueEnabled } from '@/lib/core/env/flags'
import { ensureGtmBooted } from '@/lib/site/gtm-boot'
import { isGtmEnabled } from '@/lib/site/gtm-config'
import { buildAnalyticsPageFields } from '@/lib/site/pageview-tracking'

declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: migrated from eslint suppression
    dataLayer?: Array<Record<string, any>>
    // biome-ignore lint/suspicious/noExplicitAny: migrated from eslint suppression
    gtag?: (...args: Array<any>) => void
  }
}

function isDevSilenced(): boolean {
  return (
    process.env.NODE_ENV === 'development' &&
    !isFlagValueEnabled(process.env.NEXT_PUBLIC_GTM_DEBUG)
  )
}

/**
 * GTM only mounts eagerly on funnel route groups (`<GtmRouteScope />`), but
 * conversion surfaces also live deep in the workspace (upgrade-limit dialog,
 * first-email activation, a fresh signup landing on /home). Booting the
 * container on demand for `brew_*` events keeps every conversion attributable
 * without paying the container's at-rest cost on parked workspace sessions.
 * GTM drains the pre-boot dataLayer queue on load, so the event this push is
 * about to enqueue is processed (and its `eventCallback` invoked) once the
 * script arrives. Pageviews never boot: `GtmPageviewTracker` only mounts
 * inside `<GtmRouteScope />`, and a non-conversion event is not worth waking
 * the container for.
 */
function ensureBootedForConversionEvent(data: Record<string, unknown>): void {
  if (typeof data.event !== 'string' || !data.event.startsWith('brew_')) {
    return
  }
  try {
    ensureGtmBooted()
  } catch (error) {
    // A boot failure (CSP, DOM exception) must never abort the dataLayer
    // push itself — the event stays queued for any later successful boot.
    addRumError(error, {
      feature: 'analytics',
      provider: 'google_tag_manager',
      operation: 'lazy_boot',
      outcome: 'suppressed',
    })
  }
}

// ============================================================================
// Core DataLayer Push
// ============================================================================

/**
 * GA4 tags auto-collect `page_location` from the live address bar when an
 * event fires, bypassing the sanitized custom pageview payload. Attach
 * sanitized page fields to every event so the container's parameter
 * mappings override that auto-collection (enforced by
 * `REQUIRED_GA4_EVENT_PARAMETERS` in the container audit). Explicit fields
 * on the event (e.g. the pageview payload's own values) win over these.
 */
function withSanitizedPageFields(
  data: Record<string, unknown>
): Record<string, unknown> {
  return { ...buildAnalyticsPageFields(window.location.href), ...data }
}

/**
 * Push an event to the GTM dataLayer.
 * Safe to call on server or client - only executes in browser.
 * In development mode, events are logged to the console but not pushed
 * to avoid polluting analytics data.
 */
export function pushToDataLayer(data: Record<string, unknown>): void {
  if (typeof window === 'undefined') {
    return
  }
  if (!isGtmEnabled()) {
    return
  }

  const payload = withSanitizedPageFields(data)
  if (isDevSilenced()) {
    return
  }

  try {
    ensureBootedForConversionEvent(data)
    window.dataLayer = window.dataLayer || []
    clearEventContext(window.dataLayer)
    window.dataLayer.push(payload)
  } catch (error) {
    addRumError(error, {
      feature: 'analytics',
      provider: 'google_tag_manager',
      operation: 'push_event',
      outcome: 'suppressed',
    })
  }
}

/**
 * GTM Data Layer Variables retain their previous value until explicitly
 * replaced. Clear every reusable event parameter immediately before a new
 * event so, for example, a first-email event can never inherit the value or
 * transaction ID from a checkout event fired earlier on the same page.
 */
const EMPTY_EVENT_CONTEXT = {
  transaction_id: null,
  value: null,
  currency: null,
  user_id: null,
  org_id: null,
  plan_id: null,
  plan_name: null,
  plan_price: null,
  current_plan: null,
  old_plan: null,
  new_plan: null,
  email_type: null,
  recipient_count: null,
  limit_type: null,
  usage_percent: null,
  created_at: null,
  page: null,
  page_path: null,
  page_location: null,
  page_title: null,
  page_referrer: null,
} as const

function clearEventContext(dataLayer: Array<Record<string, unknown>>): void {
  dataLayer.push({ ...EMPTY_EVENT_CONTEXT })
}

const GTM_EVENT_TIMEOUT_MS = 2000

/**
 * Push an event to the GTM dataLayer and wait for GTM to process it.
 *
 * Uses GTM's `eventCallback` to resolve once all tags triggered by the event
 * have fired. Falls back to a timeout so callers are never blocked indefinitely
 * (e.g. if GTM hasn't loaded or no tags match the event).
 *
 * Use this before hard navigations (`window.location.href`) to avoid dropping
 * high-value conversion events whose network requests are still in-flight.
 */
export function pushToDataLayerAndWait(
  data: Record<string, unknown>,
  timeoutMs: number = GTM_EVENT_TIMEOUT_MS
): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }
  if (!isGtmEnabled()) {
    return Promise.resolve()
  }

  const payload = withSanitizedPageFields(data)
  if (isDevSilenced()) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve) => {
    try {
      // Lazy boot BEFORE enqueueing: GTM processes the queued event at boot
      // and fires `eventCallback`, normally well inside the timeout fallback.
      ensureBootedForConversionEvent(data)
      window.dataLayer = window.dataLayer || []
      clearEventContext(window.dataLayer)

      const fallback = setTimeout(resolve, timeoutMs)

      window.dataLayer.push({
        ...payload,
        eventCallback: () => {
          clearTimeout(fallback)
          resolve()
        },
        eventTimeout: timeoutMs,
      })
    } catch (error) {
      addRumError(error, {
        feature: 'analytics',
        provider: 'google_tag_manager',
        operation: 'push_event_and_wait',
        outcome: 'suppressed',
      })
      resolve()
    }
  })
}

// ============================================================================
// Google Consent Mode v2
// ============================================================================

type ConsentState = 'granted' | 'denied'

interface ConsentUpdate {
  ad_storage?: ConsentState
  ad_user_data?: ConsentState
  ad_personalization?: ConsentState
  analytics_storage?: ConsentState
}

/**
 * Update Google Consent Mode v2 consent state.
 * Call this when the user grants or revokes consent via a cookie banner.
 *
 * Example:
 *   grantAllConsent();          // User accepts all cookies
 *   updateConsent({             // User accepts only analytics
 *     analytics_storage: "granted",
 *   });
 */
export function updateConsent(consent: ConsentUpdate): void {
  if (typeof window === 'undefined') {
    return
  }
  if (!isGtmEnabled()) {
    return
  }

  try {
    // Use the gtag function if available (properly handles Consent Mode v2)
    // Otherwise fall back to dataLayer push with proper object format
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', consent)
    } else {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'consent',
        update: consent,
      })
    }
  } catch (error) {
    addRumError(error, {
      feature: 'analytics',
      provider: 'google_tag_manager',
      operation: 'update_consent',
      outcome: 'suppressed',
    })
  }
}

/**
 * Track a campaign-level checkout cancellation. Used by the
 * `?billing=canceled` return path so we can attribute drop-off in GTM.
 */
export function trackCheckoutCanceled(data: {
  user_id?: string
  org_id?: string
  plan_id?: string
}): void {
  pushToDataLayer({ event: 'brew_checkout_canceled', ...data })
}

/**
 * Grant all consent categories. Call when user accepts all cookies.
 */
export function grantAllConsent(): void {
  updateConsent({
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  })
}

/**
 * Deny all consent categories. Call when user rejects all cookies.
 */
export function denyAllConsent(): void {
  updateConsent({
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  })
}

// ============================================================================
// Event Types - Add new events here for type safety
// ============================================================================

/**
 * Signup conversion event
 * Triggered when a new user signs up (within 5 minutes of account creation)
 */
export interface SignupConversionEvent {
  event: 'brew_signup_conversion'
  transaction_id: string
  user_id: string
  value: number
  currency: string
  plan_name: string
  created_at: number
}

/**
 * Checkout started event
 * Triggered when user enters the checkout flow
 */
export interface CheckoutStartedEvent {
  event: 'brew_checkout_started'
  transaction_id: string
  user_id: string
  org_id?: string
  plan_id: string
  plan_name: string
  plan_price: number
  value: number
  currency: string
  current_plan?: string
}

/**
 * Subscription started event
 * Triggered when a user subscribes for the first time (Free -> Paid).
 * This is the highest-value conversion event (first purchase).
 */
export interface SubscriptionStartedEvent {
  event: 'brew_subscription_started'
  user_id: string
  org_id?: string
  transaction_id: string
  plan_id: string
  plan_name: string
  new_plan: string
  plan_price: number
  currency: string
  value: number
}

/**
 * Subscription upgraded event
 * Triggered when a paying user upgrades to a higher paid tier (e.g. Starter -> Pro).
 * Tracks upsell revenue.
 */
export interface SubscriptionUpgradedEvent {
  event: 'brew_subscription_upgraded'
  user_id: string
  org_id?: string
  transaction_id: string
  plan_id: string
  plan_name: string
  old_plan: string
  new_plan: string
  plan_price: number
  currency: string
  value: number
}

/**
 * Usage limit hit event
 * Triggered when user hits 100% of their plan limit
 */
export interface UsageLimitHitEvent {
  event: 'brew_usage_limit_hit'
  transaction_id: string
  user_id?: string
  org_id?: string
  limit_type: string
  current_plan: string
  usage_percent: number
}

/**
 * Pricing page viewed event
 * Triggered when user views the pricing/billing page
 */
export interface PricingViewedEvent {
  event: 'brew_pricing_viewed'
  user_id?: string
  org_id?: string
  current_plan: string
  usage_percent: number
}

/**
 * First email sent event
 * Triggered when user sends their first email (activation milestone).
 *
 * `email_type` is narrowed to the surfaces that actually fire the event
 * today (`campaign`, `preview`). Re-introduce additional values
 * (`transactional`, `automation`) ONLY when a real call-site exists for
 * them, otherwise GTM dashboards end up with mystery enum values that
 * never appear in production data.
 */
export interface FirstEmailSentEvent {
  event: 'brew_first_email_sent'
  transaction_id: string
  user_id: string
  org_id?: string
  value: number
  currency: string
  email_type: 'campaign' | 'preview'
  recipient_count: number
}

// Union type of all GTM events
export type GTMEvent =
  | SignupConversionEvent
  | CheckoutStartedEvent
  | SubscriptionStartedEvent
  | SubscriptionUpgradedEvent
  | UsageLimitHitEvent
  | PricingViewedEvent
  | FirstEmailSentEvent

// ============================================================================
// Typed Event Helpers
// ============================================================================

/**
 * Track signup conversion via dataLayer.
 * GTM should be configured with a Google Ads Conversion tag triggered
 * by this custom event.
 */
export function trackSignupConversion(
  data: Omit<SignupConversionEvent, 'event'>
): void {
  pushToDataLayer({ event: 'brew_signup_conversion', ...data })
}

/**
 * Track checkout started
 */
export function trackCheckoutStarted(
  data: Omit<CheckoutStartedEvent, 'event'>
): Promise<void> {
  return pushToDataLayerAndWait({ event: 'brew_checkout_started', ...data })
}

/**
 * Track usage limit hit
 */
export function trackUsageLimitHit(
  data: Omit<UsageLimitHitEvent, 'event'>
): void {
  pushToDataLayer({ event: 'brew_usage_limit_hit', ...data })
}

/**
 * Track pricing page viewed
 */
export function trackPricingViewed(
  data: Omit<PricingViewedEvent, 'event'>
): void {
  pushToDataLayer({ event: 'brew_pricing_viewed', ...data })
}

/**
 * Track first email sent
 */
export function trackFirstEmailSent(
  data: Omit<FirstEmailSentEvent, 'event'>
): void {
  pushToDataLayer({ event: 'brew_first_email_sent', ...data })
}

// ============================================================================
// Subscription Change - Shared helper
// ============================================================================

export interface SubscriptionChangePlan {
  id: string
  name: string
  fee: { amount: number; currency: string }
}

export interface SubscriptionChangeInput {
  user_id: string
  org_id?: string
  active_plan_name: string
  transaction_id: string
  /** Actual amount charged, in major currency units (for example, 27.42). */
  transaction_value: number
  transaction_currency: string
  new_plan: SubscriptionChangePlan
}

/** Pure wire-contract builder shared by tracking and unit tests. */
export function buildSubscriptionChangeEvent(
  input: SubscriptionChangeInput
): SubscriptionStartedEvent | SubscriptionUpgradedEvent {
  const planPrice = input.new_plan.fee.amount / 100
  const currency = (
    input.transaction_currency || input.new_plan.fee.currency
  ).toUpperCase()
  const newPlanDisplay = getPlanDisplayName(input.new_plan.name)
  const common = {
    user_id: input.user_id,
    ...(input.org_id ? { org_id: input.org_id } : {}),
    transaction_id: input.transaction_id,
    plan_id: input.new_plan.id,
    plan_name: newPlanDisplay,
    new_plan: newPlanDisplay,
    plan_price: planPrice,
    currency,
    value: input.transaction_value,
  }
  const isFromFree = FREE_PLAN_NAMES.has(input.active_plan_name.toLowerCase())

  if (isFromFree) {
    return { event: 'brew_subscription_started', ...common }
  }

  return {
    event: 'brew_subscription_upgraded',
    ...common,
    old_plan: getPlanDisplayName(input.active_plan_name),
  }
}

/**
 * Track a subscription change (start or upgrade).
 *
 * Determines whether the user is moving from a free plan (subscription started)
 * or upgrading between paid tiers (subscription upgraded), then fires the
 * appropriate GTM event with the Stripe-backed transaction ID supplied by the
 * verified billing flow.
 *
 * Returns a Promise that resolves once GTM has processed the event.
 * Await before navigating to avoid dropping the event.
 */
export async function trackSubscriptionChange(
  input: SubscriptionChangeInput
): Promise<void> {
  const event = buildSubscriptionChangeEvent(input)
  await pushToDataLayerAndWait({ ...event })
}

// ============================================================================
// First Email Sent - One-time activation tracking
// ============================================================================

const FIRST_EMAIL_SENT_KEY = 'gtm_first_email_sent_tracked'

function readFirstEmailTrackedUsers(): Array<string> {
  try {
    const stored = localStorage.getItem(FIRST_EMAIL_SENT_KEY)
    if (!stored) {
      return []
    }
    const parsed = JSON.parse(stored) as unknown
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : []
  } catch {
    return []
  }
}

/**
 * Track first email sent - only fires once per user
 * Uses localStorage to ensure deduplication
 */
export function trackFirstEmailSentOnce(data: {
  user_id: string
  org_id?: string
  email_type: 'campaign' | 'preview'
  recipient_count: number
}): void {
  if (typeof window === 'undefined') {
    return
  }
  if (!isGtmEnabled()) {
    return
  }

  const trackedUsers = readFirstEmailTrackedUsers()
  if (trackedUsers.includes(data.user_id)) {
    return
  }

  trackFirstEmailSent({
    ...data,
    transaction_id: `first-email-${data.user_id}`,
    value: 0,
    currency: 'USD',
  })

  try {
    trackedUsers.push(data.user_id)
    const trimmed = trackedUsers.slice(-100)
    localStorage.setItem(FIRST_EMAIL_SENT_KEY, JSON.stringify(trimmed))
  } catch {
    // Storage can be blocked or full. The event already fired with a stable
    // transaction ID, so Google can still deduplicate a later retry.
  }
}
